import { AfterViewInit, Component, OnInit } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from "@angular/forms";
import { Address } from "@models/address/address";
import { EntityType } from "@models/common/entity-type.enum";
import { Company } from "@models/companies/company";
import { EntityData } from "@models/companies/entity-data";
import { Document } from "@models/documents/document";
import { EntityChangeData } from "@models/documents/entity-change-data";
import { IndividualData } from "@models/individuals/individual-data";
import {
  Relationship,
  ShareholderRelationshipDetails,
  TrusteeRelationshipDetails
} from "@models/relationships/relationship";
import { RelationshipType, RelationshipTypeLabels } from "@models/relationships/relationship-type.enum";
import { SecurityHolding } from "@models/shares/security-holding";
import {
  CorporateHolderModel,
  IndividualHolderModel,
  SecurityRegistryRecord
} from "@models/shares/security-registry-record";
import { SecurityType } from "@models/shares/security-type";
import { Trust } from "@models/trusts/trust";
import {
  IndividualCorporateRelationship,
  individualCorporateTrusteeOptions
} from "@models/trusts/trust-change-relationship";
import { jurisdictionOptions, JurisdictionType, TrustType } from "@models/trusts/trusts-enums";
import { SelectOption } from "@models/ui-models/select-options";
import { DividerComponent } from "@shared/components/commons/divider/divider.component";
import { ButtonComponent } from "@shared/controls/button/button.component";
import { CheckboxGroupComponent } from "@shared/controls/checkbox-group/checkbox-group.component";
import { CheckboxComponent } from "@shared/controls/checkbox/checkbox.component";
import { FlagsAutocompleteComponent } from "@shared/controls/flags-autocomplete/flags-autocomplete.component";
import { InputNumberComponent } from "@shared/controls/input-number/input-number.component";
import { InputComponent } from "@shared/controls/input/input.component";
import { SelectComponent } from "@shared/controls/select/select.component";
import { ValidationErrorComponent } from "@shared/controls/validation-error/validation-error.component";
import { YesNoControlComponent } from "@shared/controls/yes-no-control-component/yes-no-control.component";
import { CountriesService } from "@shared/services/countries.service";
import { clearFormArray } from "@shared/utils/clear-form-array";
import { Guid } from "@shared/utils/guid.helper";
import { parseFullName } from "@shared/utils/parse-fullname";
import { setControlDisabled } from "@shared/utils/set-control-disabled";
import { CustomFormValidators } from "@shared/validators/custom-form-validators";
import { NumbersValidators } from "@shared/validators/numbers.validators";
import { startWith } from "rxjs";
import {
  IntegrationsEntitySearchControlComponent
} from "../../../controls/integrations-entity-search-control/integrations-entity-search-control.component";
import {
  ShareJointSecurityHolderComponent
} from "../../../controls/share-joint-security-holder/share-joint-security-holder.component";
import {
  ShareSecurityTypesDropdownComponent
} from "../../../controls/share-security-types-dropdown/share-security-types-dropdown.component";
import { SecurityholderFormGroup, SecurityHoldingFormGroup } from "../../../models/company/company-registration.model";
import {
  customErrorsOfShareCancellationNumbers,
  totalNumberOfSharesCustomErrors
} from "../../../models/shares/company-security-cancellation.model";
import {
  getNewShareholderTypesSelectOptions,
  ShareholderTypesEnum
} from "../../../models/shares/company-security-issue.model";
import { TrustUnitEstablishment } from "../../../models/trusts/trust-unit-establishment.model";
import {
  AddressFormGroupComponent
} from "../../../reusable-form-groups/address-form-group/address-form-group.component";
import {
  CompanyNameAcnFormGroupComponent
} from "../../../reusable-form-groups/company-name-acn-form-group/company-name-acn-form-group.component";
import {
  CorporateRelationshipFormGroupComponent
} from "../../../reusable-form-groups/corporate-relationship-form-group/corporate-relationship-form-group.component";
import {
  IndividualDataFormGroupComponent,
  IndividualDataFormGroupControls
} from "../../../reusable-form-groups/individual-data-form-group/individual-data-form-group.component";
import {
  IndividualRelationshipFormGroupComponent
} from "../../../reusable-form-groups/relationship-form-group/individual-relationship-form-group.component";
import {
  AuthorisedSignatoriesStepComponent
} from "../../../reusable-form-parts/authorised-signatories-step/authorised-signatories-step.component";
import { StepperFormComponent, StepperFormDescriptionComponent } from "../../stepper-form";
import {
  BaseTrustEstablishmentFormComponent
} from "../base-trust-establishment-form/base-trust-establishment-form.component";
import { getTrustEstablishmentChangeType } from "../constants/trust-establishment-texts";
import {
  IIndividualCorporateRelationshipFormGroup
} from "../trust-discretionary-establishment/trust-discretionary-establishment.component";
import {
  UnitTrustEstablishmentFormSteps,
  UnitTrustEstablishmentFormStepsEnum
} from "./constants/unit-trust-establishment-form-steps";
import { unitTrustShareTypesOptions } from "./constants/unit-trust-share-types-options";

@Component({
  selector: 'app-unit-trust-establishment-form',
  standalone: true,
  imports: [
    StepperFormComponent,
    StepperFormDescriptionComponent,
    ReactiveFormsModule,
    AddressFormGroupComponent,
    FlagsAutocompleteComponent,
    InputNumberComponent,
    IntegrationsEntitySearchControlComponent,
    CheckboxGroupComponent,
    CorporateRelationshipFormGroupComponent,
    IndividualRelationshipFormGroupComponent,
    SelectComponent,
    ButtonComponent,
    CheckboxComponent,
    CompanyNameAcnFormGroupComponent,
    DividerComponent,
    IndividualDataFormGroupComponent,
    InputComponent,
    ShareJointSecurityHolderComponent,
    ShareSecurityTypesDropdownComponent,
    ValidationErrorComponent,
    YesNoControlComponent,
    AuthorisedSignatoriesStepComponent
  ],
  templateUrl: 'trust-unit-establishment.component.html',
  styleUrls: [
    'trust-unit-establishment.component.scss',
    '../../stepper-form/base-stepper-component/base-stepper-form.component.scss',
  ]
})
export class TrustUnitEstablishmentComponent extends BaseTrustEstablishmentFormComponent<UnitTrustEstablishmentFormStepsEnum, TrustUnitEstablishment> implements OnInit, AfterViewInit {
  readonly EntityType = EntityType;
  readonly IndividualCorporateRelationship = IndividualCorporateRelationship;
  readonly ShareholderTypesEnum = ShareholderTypesEnum;
  readonly jurisdictionOptions = jurisdictionOptions;
  readonly individualCorporateTrusteeOptions = individualCorporateTrusteeOptions;
  readonly totalNumberOfSharesCustomErrors = totalNumberOfSharesCustomErrors;
  readonly newShareholderTypesSelectOptions = getNewShareholderTypesSelectOptions('Joint Unitholder');
  readonly customErrorsOfShareCancellationNumbers = customErrorsOfShareCancellationNumbers;
  readonly hiddenIndividualControls: (keyof IndividualDataFormGroupControls)[] = ['formerName', 'dob', 'birthCity', 'birthCountry'];
  readonly individualDataFormLabels: Record<keyof Partial<IndividualDataFormGroupControls>, string> = { fullName: 'Individual Name' } as Record<keyof Partial<IndividualDataFormGroupControls>, string>;
  readonly unitTrustShareTypesOptions: SelectOption<string>[] = unitTrustShareTypesOptions;


  override StepsEnum = UnitTrustEstablishmentFormStepsEnum;
  override steps = UnitTrustEstablishmentFormSteps;

  relationshipTypesOptions: SelectOption[] = [
    { label: RelationshipTypeLabels[RelationshipType.Trustee], value: RelationshipType.Trustee, disabled: true },
    { label: 'Unitholder', value: RelationshipType.Securityholder },
  ];
  isSimpleUnitholdersFillingMode = true;
  securityTypes: SecurityType[] = [new SecurityType({
    securityTypeId: Guid.generate(),
    entityId: this.companyChangeData.entityId
  })];
  securityTypesRecord: Record<string, SecurityType> = { [this.securityTypes[0].securityTypeId]: this.securityTypes[0] };
  authorisedSecurityRegistryRecords: SecurityRegistryRecord[] = [];

  override stepperForm = new FormGroup({
    [this.StepsEnum.FormDescription]: new FormGroup({}),
    [this.StepsEnum.TrustDetailsAndAddress]: new FormGroup({
      trustName: new FormControl<string | null>(null, [Validators.required, CustomFormValidators.maxLength(50)]),
      jurisdiction: new FormControl<JurisdictionType | null>(null, [Validators.required]),
      meetingAddress: AddressFormGroupComponent.defineForm()
    }),
    [this.StepsEnum.Trustees]: new FormGroup({
      trustees: new FormArray<IIndividualCorporateRelationshipFormGroup>([this.createTrusteeFormGroup({ type: RelationshipType.Trustee }, true)])
    }),
    [this.StepsEnum.Unitholders]: new FormGroup({
      unitholders: new FormArray<SecurityholderFormGroup>([this.createUnitholderFormGroup()])
    }),
    [this.StepsEnum.AuthorisedSignatories]: AuthorisedSignatoriesStepComponent.defineForm(),
  });

  override ngOnInit(): void {
    super.ngOnInit();
    this.isSimpleUnitholdersFillingMode = !this.isEdit;
    this.redirectAfterSubmit = true;

    this.securityTypes = this.securityTypes.map((type) => {
      type.entityId = this.entityId;
      return type;
    });
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.setupChange();
  }

  override setCurrentStep(newStepIndex: number): void {
    super.setCurrentStep(newStepIndex);
    this.updateFormSteps();

    if (this.currentStep == this.StepsEnum.Unitholders && this.isSimpleUnitholdersFillingMode) {
      const trusteeUnitholdersFormGroups = this.trusteesFormArray.controls
        .filter((unitholderFormGroup) => unitholderFormGroup.controls.types.value.includes(RelationshipType.Securityholder));

      if (trusteeUnitholdersFormGroups.length) {
        clearFormArray(this.unitholdersFormArray);
        trusteeUnitholdersFormGroups.forEach((trusteeFormGroup) => {
          const shareholderFormGroup = this.createUnitholderFormGroup();
          const securityTypes = [{ securityTypeId: this.securityTypes[0].securityTypeId, number: 1 }];

          shareholderFormGroup.patchValue({
            securityTypes,
            shareholderType: ShareholderTypesEnum.Individual,
            individualId: trusteeFormGroup.controls.individualId.value,
            individualShareholder: {
              fullName: trusteeFormGroup.controls.individualTrustee.controls.name.value,
              address: trusteeFormGroup.controls.individualTrustee.controls.address.value,
            },
          });

          this.unitholdersFormArray.push(shareholderFormGroup);
          setControlDisabled(shareholderFormGroup);
        });
      } else {
        this.setIsSimpleShareholdersFillingMode(false);
      }
    }

    if (this.currentStep == this.StepsEnum.AuthorisedSignatories) {
      this.fillAuthorisedSignatories();
    }
  }

  override updateFormSteps(): void {
    this.steps = UnitTrustEstablishmentFormSteps.filter((step) => {

      if (step.step == this.StepsEnum.AuthorisedSignatories) {
        return this.isAuthorisedSignatoriesStepVisible();
      }

      return true;
    });
  }

  isAuthorisedSignatoriesStepVisible(): boolean {
    const isTrusteeCorporate = this.trusteesFormArray.length == 1 && this.trusteesFormArray.at(0).controls.trusteeType.value == IndividualCorporateRelationship.Corporate;
    const securityHoldings = this.buildUnitholdersPayload();
    const someHolderIsCorporateHolder = securityHoldings?.some((securityHolding) => {
      return securityHolding.relationships?.some((holder) => holder.entityDataOverride);
    });

    return someHolderIsCorporateHolder || isTrusteeCorporate;
  }

  override afterSubmit(changes: EntityChangeData[]) {
    const trustUnitEstablishmentChange = changes.find((change) => change.$type === TrustUnitEstablishment.$type) as TrustUnitEstablishment;
    this.setupChange(trustUnitEstablishmentChange);
  }

  override setupChange(change: TrustUnitEstablishment = this.formModel) {
    if (!this.isEdit && change === this.formModel) return;
    clearFormArray(this.trusteesFormArray);

    this.trustDetailsForm.patchValue(change);

    this.setupTrusteeChange(change);
    this.setupUnitholdersChange(change);
    this.updateFormSteps();
  }

  setupTrusteeChange(change: TrustUnitEstablishment): void {
    const uniqueUnitholdersMap = new Map<string, Relationship>();

    change.securityHoldings
      .forEach((holding) => {
        holding.relationships?.forEach((relationship) => uniqueUnitholdersMap.set(relationship.relationshipId, relationship));
      });

    const relationshipsByTypes = [...change.trustees, ...uniqueUnitholdersMap.values()]
      .reduce((relationships, relationship) => {
        const id = relationship.individualId ?? relationship.entityId ?? relationship.relationshipId;

        if (id == Guid.EmptyGuid) {
          return relationships;
        }

        if (relationships[id]) {
          relationships[id].push(relationship);
        } else {
          relationships[id] = [relationship];
        }

        return relationships;
      }, {} as Record<string, Relationship[]>);

    const trusteesFormGroups = Object.values(relationshipsByTypes)
      .filter((relationships) => relationships.some((relationship) => relationship.type == RelationshipType.Trustee))
      .reduce((forms, relationships, index) => {
        const trusteeFormGroup = this.createTrusteeFormGroup(relationships[0], index == 0);
        const types = relationships.map((relationship) => relationship.type);

        trusteeFormGroup.patchValue({ types });
        forms.push(trusteeFormGroup);

        if (relationships[0].entityDataOverride && relationships[0].details?.$type == TrusteeRelationshipDetails.$type) {
          const details = relationships[0].details as TrusteeRelationshipDetails;
          trusteeFormGroup.controls.corporateTrustee.controls.authorisedSignatories.patchValue(details.authorisedSignatories ?? []);
        }

        return forms;
      }, [] as IIndividualCorporateRelationshipFormGroup[]);

    trusteesFormGroups.forEach((trusteeFormGroup) => this.trusteesFormArray.push(trusteeFormGroup));
  }

  setupUnitholdersChange(change: TrustUnitEstablishment): void {
    this.disabledUnnecessaryControls();
    clearFormArray(this.unitholdersFormArray);

    const securityTypes: SecurityType[] = [];
    const securityTypesRecord: Record<string, SecurityType> = {};
    const relationshipsGroups: Record<string, Relationship[]> = {};
    const relationshipsGroupsHoldings: Record<string, SecurityHolding[]> = {};

    change.securityHoldings.forEach((securityHolding) => {
      if (!securityTypesRecord[securityHolding.securityType.securityTypeId]) {
        securityTypesRecord[securityHolding.securityType.securityTypeId] = securityHolding.securityType;
        securityTypes.push(securityHolding.securityType);
      }

      const relationshipGroupKey = securityHolding.relationships!.map((relationship) => relationship.relationshipId).join();

      if (relationshipsGroupsHoldings[relationshipGroupKey]) {
        relationshipsGroupsHoldings[relationshipGroupKey].push(securityHolding);
      } else {
        relationshipsGroupsHoldings[relationshipGroupKey] = [securityHolding];
        relationshipsGroups[relationshipGroupKey] = securityHolding.relationships!;
      }
    });

    this.securityTypes = securityTypes;
    this.securityTypesRecord = securityTypesRecord;

    Object.entries(relationshipsGroups)
      .forEach(([relationshipGroupKey, relationships]) => {
        const shareholdersForm = this.createUnitholderFormGroup();
        const relationship = relationships[0];
        const shareholderDetails = relationship?.details as ShareholderRelationshipDetails;

        shareholdersForm.controls.isNotBeneficialOwner.setValue(!shareholderDetails?.isBeneficialOwner);
        shareholdersForm.controls.beneficialOwner.setValue(shareholderDetails?.beneficialOwner);
        clearFormArray(shareholdersForm.controls.securityTypes);

        if (relationships.length > 1) {
          shareholdersForm.controls.shareholderType.setValue(ShareholderTypesEnum.JointShareholder);
          const holders: (IndividualHolderModel | CorporateHolderModel)[] = relationshipsGroups[relationshipGroupKey].map((relationship) => {
            if (relationship.individualDataOverride) {
              return new IndividualHolderModel({
                individualId: relationship.individualId!,
                individualData: relationship.individualDataOverride,
              });
            }

            return new CorporateHolderModel({
              entityId: relationship.entityId,
              entityData: relationship.entityDataOverride!
            });
          });
          shareholdersForm.controls.joint.setValue(holders);
        } else {
          const relationship = relationships[0];

          if (relationship?.entityDataOverride) {
            shareholdersForm.controls.shareholderType.setValue(ShareholderTypesEnum.Corporate);
            shareholdersForm.patchValue({
              name: relationship.entityDataOverride.name,
              acn: relationship.entityDataOverride.entityNumber,
              isOverseasCompany: !CountriesService.isAustralianStateOrAustralia(relationship.entityDataOverride.registeredAddress.country),
              registeredAddress: relationship.entityDataOverride.registeredAddress,
            });
          } else {
            shareholdersForm.controls.shareholderType.setValue(ShareholderTypesEnum.Individual);
            shareholdersForm.controls.individualId.setValue(relationship.individualId);
            shareholdersForm.controls.individualShareholder.patchValue({
              ...relationship?.individualDataOverride,
              address: relationship?.individualDataOverride?.address,
              formerFullName: relationship.individualDataOverride?.formerName.fullNameWithMiddle ?? ''
            });
          }
        }

        relationshipsGroupsHoldings[relationshipGroupKey].forEach((holding) => {
          const securityTypeFormGroup = this.createSecurityTypeFormGroup(holding);
          shareholdersForm.controls.securityTypes.push(securityTypeFormGroup);
        });

        shareholdersForm.controls.shareholderType.setValue(shareholdersForm.controls.shareholderType.value);
        shareholdersForm.controls.isNotBeneficialOwner.setValue(shareholdersForm.controls.isNotBeneficialOwner.value);
        this.unitholdersFormArray.push(shareholdersForm);
      });

    this.unitholdersFormArray.updateValueAndValidity();
  }

  onTrustSelected(trust: Trust): void {
    this.trustDetailsForm.patchValue({
      ...trust,
      trustName: trust.name
    });
  }

  createUnitholderFormGroup(): SecurityholderFormGroup {
    const securityholderFormGroup = new FormGroup({
      shareholderType: new FormControl<ShareholderTypesEnum | null>(null, [Validators.required]),
      isNotBeneficialOwner: new FormControl<boolean | null>(false, [Validators.required]),
      beneficialOwner: new FormControl<string | null>(null, [Validators.required]),

      individualId: new FormControl<string | null>(null),
      individualShareholder: IndividualDataFormGroupComponent.defineForm(),

      ...CompanyNameAcnFormGroupComponent.defineFormControls(),
      isOverseasCompany: new FormControl<boolean | null>(null),
      registeredAddress: AddressFormGroupComponent.defineForm(),
      authSignatories: new FormControl<IndividualData[] | null>(null),

      joint: new FormControl<(IndividualHolderModel | CorporateHolderModel)[] | null>(null, [Validators.required]),

      securityTypes: new FormArray<SecurityHoldingFormGroup>([this.createSecurityTypeFormGroup()]),
      relationshipId: new FormControl<string>(Guid.generate(), { nonNullable: true }),
    });

    this.listenShareholderTypeChanges(securityholderFormGroup);
    this.listenIsBeneficialOwnerChanges(securityholderFormGroup);

    // TODO: provide after demo
    // this.listenIndividualShareholderFullNameChanges(securityholderFormGroup);
    // this.listenShareholderAddressChanges(securityholderFormGroup);

    return securityholderFormGroup;
  }

  listenShareholderTypeChanges(shareholderForm: SecurityholderFormGroup): void {
    shareholderForm.controls.shareholderType.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((shareholderType) => {
        if (shareholderType === null) {
          return;
        }

        // set state of the new Individual Shareholder control
        setControlDisabled(shareholderForm.controls.individualShareholder, shareholderType !== ShareholderTypesEnum.Individual);
        this.hiddenIndividualControls.forEach(key => setControlDisabled(shareholderForm.controls.individualShareholder.controls[key]));

        // set state of the new Corporate Shareholder control
        setControlDisabled(shareholderForm.controls.acn, shareholderType !== ShareholderTypesEnum.Corporate);
        setControlDisabled(shareholderForm.controls.name, shareholderType !== ShareholderTypesEnum.Corporate);
        setControlDisabled(shareholderForm.controls.isOverseasCompany, shareholderType !== ShareholderTypesEnum.Corporate);
        setControlDisabled(shareholderForm.controls.registeredAddress, shareholderType !== ShareholderTypesEnum.Corporate);

        // set state of the new Joint Shareholder control
        setControlDisabled(shareholderForm.controls.joint, shareholderType !== ShareholderTypesEnum.JointShareholder);
      });
  }

  listenIsBeneficialOwnerChanges(shareholderForm: SecurityholderFormGroup): void {
    shareholderForm.controls.isNotBeneficialOwner.valueChanges
      .pipe(
        startWith(shareholderForm.controls.isNotBeneficialOwner.value),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((isSharesOwnedOnBehalf) => {
        setControlDisabled(shareholderForm.controls.beneficialOwner, !isSharesOwnedOnBehalf);
      });
  }

  // TODO: provide after demo
  // listenIndividualShareholderFullNameChanges(securityholderFormGroup: SecurityholderFormGroup): void {
  //   securityholderFormGroup.controls.individualShareholder.controls.fullName.valueChanges
  //     .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(100))
  //     .subscribe(() => this.updateSuggestedFullNames());
  //
  //   securityholderFormGroup.controls.joint.valueChanges
  //     .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(100))
  //     .subscribe(() => this.updateSuggestedFullNames());
  // }

  // TODO: provide after demo
  // listenShareholderAddressChanges(securityholderFormGroup: SecurityholderFormGroup): void {
  //   securityholderFormGroup.controls.registeredAddress.valueChanges
  //     .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(100))
  //     .subscribe(() => this.updateSuggestedAddresses());
  //
  //   securityholderFormGroup.controls.individualShareholder.controls.address.valueChanges
  //     .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(100))
  //     .subscribe(() => this.updateSuggestedAddresses());
  //
  //   securityholderFormGroup.controls.joint.valueChanges
  //     .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(100))
  //     .subscribe(() => this.updateSuggestedAddresses());
  // }

  createSecurityTypeFormGroup(securityHolding: SecurityHolding | null = null): SecurityHoldingFormGroup {
    const securityHoldingFormGroup = new FormGroup({
      securityTypeId: new FormControl<string | null>(securityHolding?.securityType?.securityTypeId ?? null, [Validators.required]),
      number: new FormControl<number | null>(securityHolding?.number ?? null, [Validators.required, NumbersValidators.min(0), NumbersValidators.isInteger]),
      paid: new FormControl<number | null>(securityHolding?.paid ?? null, [Validators.required, NumbersValidators.min(0)]),
      unpaid: new FormControl<number | null>(securityHolding?.unpaid ?? null, [Validators.required, NumbersValidators.min(0)])
    });

    this.setCustomSecurityTypeFormValidators(securityHoldingFormGroup);
    this.listenShareholdingNumberChange(securityHoldingFormGroup);

    return securityHoldingFormGroup;
  }

  private setCustomSecurityTypeFormValidators(securityTypeFormGroup: SecurityHoldingFormGroup): void {
    const paidTotalValidator: ValidatorFn = () => {
      const selectedSecurityType = this.getSelectedSecurityType(securityTypeFormGroup);
      if (selectedSecurityType === null || !securityTypeFormGroup.value.paid || !securityTypeFormGroup.value.number) {
        return null;
      }
      // (Math.abs(prefilled total paid - total paid))/prefilled total paid < 0.05

      const calculatedTotalPaid = selectedSecurityType.paidAmount * securityTypeFormGroup.value.number;
      return (Math.abs(calculatedTotalPaid - securityTypeFormGroup.value.paid) / calculatedTotalPaid) < 0.05
        ? null
        : { paidChangeIsBiggerThan5Percent: true };
    };

    const unpaidTotalValidator: ValidatorFn = () => {
      const selectedSecurityType = this.getSelectedSecurityType(securityTypeFormGroup);
      if (selectedSecurityType === null || !securityTypeFormGroup.value.unpaid || !securityTypeFormGroup.value.number) {
        return null;
      }
      // (Math.abs(prefilled total unpaid - total unpaid))/prefilled total unpaid < 0.05

      const calculatedTotalPaid = selectedSecurityType.unpaidAmount * securityTypeFormGroup.value.number;
      return (Math.abs(calculatedTotalPaid - securityTypeFormGroup.value.unpaid) / calculatedTotalPaid) < 0.05
        ? null
        : { unpaidChangeIsBiggerThan5Percent: true };
    };

    // total paid + total unpaid > 0
    const paidUnpaidSumValidator: ValidatorFn = () => {
      const totalPaid = securityTypeFormGroup.controls.paid.getRawValue();
      const totalUnpaid = securityTypeFormGroup.controls.unpaid.getRawValue();
      if (totalPaid === null || totalUnpaid === null) {
        return null;
      }

      return (totalPaid + totalUnpaid) > 0
        ? null
        : { paidUnpaidSumIsNotPositive: true };
    };

    securityTypeFormGroup.setValidators([
      paidTotalValidator,
      unpaidTotalValidator,
      paidUnpaidSumValidator
    ]);
  }

  listenShareholdingNumberChange(securityTypeFormGroup: SecurityHoldingFormGroup): void {
    securityTypeFormGroup.controls.number.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((numberIncrease) => {
        const selectedSecurityType = this.getSelectedSecurityType(securityTypeFormGroup);

        if (numberIncrease === null || selectedSecurityType === null) {
          return;
        }

        securityTypeFormGroup.controls.paid.patchValue(numberIncrease * selectedSecurityType.paidAmount);
        securityTypeFormGroup.controls.unpaid.patchValue(numberIncrease * selectedSecurityType.unpaidAmount);
      });
  }

  getSelectedSecurityType(securityTypeFormGroup: SecurityHoldingFormGroup): SecurityType | null {
    return this.securityTypesRecord[securityTypeFormGroup.controls.securityTypeId.value!] ?? null;
  }

  setIsSimpleShareholdersFillingMode(isSimpleUnitholdersFillingMode: boolean): void {
    this.isSimpleUnitholdersFillingMode = isSimpleUnitholdersFillingMode;

    if (isSimpleUnitholdersFillingMode) return;

    // Enable shareholder type and set it's value to itself
    // the rest of job performs valueChanges listeners of form
    this.unitholdersFormArray.controls.forEach((shareholderFormGroup) => {
      shareholderFormGroup.controls.shareholderType.enable();
      shareholderFormGroup.controls.securityTypes.enable();
      shareholderFormGroup.controls.isNotBeneficialOwner.enable();
      shareholderFormGroup.controls.shareholderType.setValue(shareholderFormGroup.controls.shareholderType.value);
      shareholderFormGroup.controls.individualId.setValue(null);
    });
  }

  removeUnitholderShareHolding(shareholderIndex: number, shareholdingIndex: number): void {
    this.unitholdersFormArray.controls[shareholderIndex].controls.securityTypes.removeAt(shareholdingIndex);
  }

  addSecurityHolding(shareholderIndex: number): void {
    this.unitholdersFormArray.controls[shareholderIndex].controls.securityTypes.push(this.createSecurityTypeFormGroup());
  }

  removeShareholder(shareholderIndex: number): void {
    if (this.isSimpleUnitholdersFillingMode) return;
    this.unitholdersFormArray.removeAt(shareholderIndex);
  }

  addShareholder(): void {
    if (this.isSimpleUnitholdersFillingMode) return;
    const form = this.createUnitholderFormGroup();
    this.unitholdersFormArray.push(form);
  }

  addShareType(shareholderIndex: number, securityHoldingIndex: number, newSecurityHolding: SecurityType): void {
    newSecurityHolding.entityId = this.entityId;
    this.securityTypes.push(newSecurityHolding);
    this.securityTypesRecord[newSecurityHolding.securityTypeId] = newSecurityHolding;
    this.unitholdersFormArray
      .controls[shareholderIndex]
      .controls.securityTypes.controls[securityHoldingIndex]
      .controls.securityTypeId?.setValue(newSecurityHolding.securityTypeId);
  }

  disabledUnnecessaryControls(): void {
    this.hiddenIndividualControls.forEach((key) => {
      this.unitholdersFormArray.controls.forEach((shareholderFormGroup) => {
        setControlDisabled(shareholderFormGroup.controls.individualShareholder.controls[key]);
      });
    });
  }

  override buildDocument(): Document | null {
    const trustees = this.trusteesFormArray.controls.map((trusteeFormGroup) => {
      if (trusteeFormGroup.controls.trusteeType.value == IndividualCorporateRelationship.Corporate) {
        const authorisedSignatories = trusteeFormGroup.controls.corporateTrustee.controls.authorisedSignatories.value ?? [];

        return new Relationship({
          type: RelationshipType.Trustee,
          relationshipId: Guid.generate(),
          entityId: Guid.generate(),
          entityDataOverride: new Company({
            acn: trusteeFormGroup.controls.corporateTrustee.controls.acn.value ?? '',
            entityNumber: trusteeFormGroup.controls.corporateTrustee.controls.acn.value ?? '',
            name: trusteeFormGroup.controls.corporateTrustee.controls.name.value ?? '',
            registeredAddress: new Address(trusteeFormGroup.controls.corporateTrustee.controls.address.value as Address),
          }),
          details: new TrusteeRelationshipDetails({ authorisedSignatories })
        });
      }

      return new Relationship({
        type: RelationshipType.Trustee,
        relationshipId: Guid.generate(),
        individualId: trusteeFormGroup.controls.individualId.value,
        individualDataOverride: new IndividualData({
          address: new Address(trusteeFormGroup.controls.individualTrustee.controls.address.value as Address),
          fullName: trusteeFormGroup.controls.individualTrustee.controls.name.value ?? '',
          ...parseFullName(trusteeFormGroup.controls.individualTrustee.controls.name.value ?? '') as Partial<IndividualData>
        }),
      });
    });

    const change = new TrustUnitEstablishment({
      trustName: this.trustDetailsForm.controls.trustName.value ?? '',
      trustType: this.trustType ?? TrustType.Discretionary,
      subType: this.trustSubtype,
      meetingAddress: new Address(this.trustDetailsForm.controls.meetingAddress.value as Address),
      jurisdiction: this.trustDetailsForm.controls.jurisdiction.value!,
      trustees,
      securityHoldings: this.buildUnitholdersPayload(),
    });

    return new Document({
      changes: [change],
      entityId: this.entityId,
      type: getTrustEstablishmentChangeType(this.trustSubtype),
      documentId: this.documentId,
    });
  }

  buildUnitholdersPayload(): SecurityHolding[] {
    const securityTypesRelationships: Record<string, Relationship[]> = {};
    const securityHoldings = this.unitholdersFormArray.controls
      .map((shareholderFormGroup) => {
        const relationshipsDetails = new ShareholderRelationshipDetails({
          isBeneficialOwner: !shareholderFormGroup.controls.isNotBeneficialOwner.value,
          beneficialOwner: shareholderFormGroup.controls.beneficialOwner.value!,
        });
        let relationships: Relationship[] = [];
        const relationshipShareHoldings: SecurityHolding[] = [];

        shareholderFormGroup.controls.securityTypes.controls.forEach((securityHoldingForm) => {
          const securityHoldingFormValue = securityHoldingForm.value;

          if (!securityTypesRelationships[securityHoldingForm.controls.securityTypeId.value!]) {
            securityTypesRelationships[securityHoldingForm.controls.securityTypeId.value!] = [];
          }

          switch (shareholderFormGroup.controls.shareholderType.value) {
            case ShareholderTypesEnum.Individual: {
              const individualDataOverride = IndividualDataFormGroupComponent.toIndividualData(shareholderFormGroup.controls.individualShareholder as FormGroup<Partial<IndividualDataFormGroupControls>>);
              relationships = [new Relationship({
                individualDataOverride,
                individualId: shareholderFormGroup.controls.individualId.value
              })];
              break;
            }
            case ShareholderTypesEnum.Corporate: {
              relationships = [new Relationship({
                entityDataOverride: new EntityData({
                  ...shareholderFormGroup.value as Partial<EntityData>,
                  entityNumber: shareholderFormGroup.value.acn!,
                }),
                relationshipId: Guid.generate()
              })];
              break;
            }
            case ShareholderTypesEnum.JointShareholder: {
              relationships = ShareJointSecurityHolderComponent.toRelationships(shareholderFormGroup.value.joint ?? []);
              break;
            }
          }

          relationships = relationships.map((relationship) => {
            relationship.type = RelationshipType.Securityholder;
            relationship.relationshipId = relationship.relationshipId ?? Guid.generate();
            relationship.details = relationshipsDetails;

            return relationship;
          });

          securityTypesRelationships[securityHoldingFormValue.securityTypeId!] = [...securityTypesRelationships[securityHoldingFormValue.securityTypeId!], ...relationships];

          relationshipShareHoldings.push(new SecurityHolding({
            entityId: this.entityId,
            securityHoldingId: Guid.EmptyGuid,
            securityHoldingAsicId: Guid.EmptyGuid,
            relationships,
            paid: securityHoldingForm.controls.paid.value!,
            unpaid: securityHoldingForm.controls.unpaid.value!,
            number: securityHoldingForm.controls.number.value!,
            securityTypeId: securityHoldingForm.controls.securityTypeId.value!,
            securityType: this.securityTypesRecord[securityHoldingForm.controls.securityTypeId.value!],
          }));
        });

        return relationshipShareHoldings;
      }).flat();

    return securityHoldings;
  }

  onCorporateShareholderSelected(company: Company, unitholderFormGroup: SecurityholderFormGroup): void {
    unitholderFormGroup.patchValue({
      name: company?.name,
      acn: company?.acn,
      isOverseasCompany: !CountriesService.isAustralianStateOrAustralia(company?.registeredAddress?.country),
      registeredAddress: company?.registeredAddress,
    });
  }

  onCorporateHolderChangeChange(holder: CorporateHolderModel): void {
    const isTrusteeFormGroupsQuantityEqualOne = this.trusteesFormArray.length == 1;
    const isTrusteeTypeCorporate = this.trusteesFormArray.at(0).controls.trusteeType.value == IndividualCorporateRelationship.Corporate;
    const isTrusteeRelationshipIdEqual = this.trusteesFormArray.at(0).controls.relationshipId.value == holder.relationshipId;
    const isTrusteeCorporateNameEqual = this.trusteesFormArray.at(0).controls.corporateTrustee.controls.name.enabled && this.trusteesFormArray.at(0).controls.corporateTrustee.controls.name.value == holder.name;

    if (isTrusteeFormGroupsQuantityEqualOne && isTrusteeTypeCorporate && (isTrusteeRelationshipIdEqual || isTrusteeCorporateNameEqual)) {
      this.trusteesFormArray.at(0).controls.corporateTrustee.controls.authorisedSignatories.setValue(holder.details.authorisedSignatories ?? null);
    }

    // change of auth signatories for shareholder type = corporate holder
    const shareholderFormToUpdate = this.unitholdersFormArray.controls.find((unitholderFormGroup) => {
      return unitholderFormGroup.controls.relationshipId.value == holder.relationshipId;
    });
    shareholderFormToUpdate?.controls.authSignatories.setValue(holder.details.authorisedSignatories ?? null);

    // change of auth signatories for shareholder type = joint corporate holder
    this.unitholdersFormArray.controls
      .forEach((shareholderForm) => {
        if (shareholderForm.controls.shareholderType.value == ShareholderTypesEnum.JointShareholder) {
          const value = shareholderForm.controls.joint.value;
          const relatedCorporateHolderIndex = value?.findIndex((h) => h.relationshipId == holder.relationshipId);

          if (relatedCorporateHolderIndex != undefined && relatedCorporateHolderIndex > -1 && value && value[relatedCorporateHolderIndex]) {
            value[relatedCorporateHolderIndex].details.authorisedSignatories = holder.details.authorisedSignatories;
            shareholderFormToUpdate?.controls.joint.setValue(value);
          }
        }
      });
  }

  fillAuthorisedSignatories(): void {
    const records: SecurityRegistryRecord[] = [];

    const firstTrusteeFormGroup = this.trusteesFormArray.at(0);
    const isFirstTrusteeCorporate = firstTrusteeFormGroup.controls.trusteeType.value == IndividualCorporateRelationship.Corporate;

    if (firstTrusteeFormGroup && isFirstTrusteeCorporate) {
      let holders: (IndividualHolderModel | CorporateHolderModel)[] = [];
      const formValue = firstTrusteeFormGroup.controls.corporateTrustee.value;
      const relationshipId = firstTrusteeFormGroup.controls.relationshipId.value ?? Guid.generate();

      if (!firstTrusteeFormGroup.controls.relationshipId.value) {
        firstTrusteeFormGroup.controls.relationshipId.setValue(relationshipId);
      }

      const entityData = new EntityData({
        name: formValue.name ?? '',
        entityNumber: formValue.acn ?? '',
        registeredAddress: new Address(formValue.address as Partial<Address>),
      });

      const details = new ShareholderRelationshipDetails({
        authorisedSignatories: formValue.authorisedSignatories ?? []
      });

      const holder = new CorporateHolderModel({
        entityData,
        relationshipId,
        details
      });

      holders = [holder];

      records.push(new SecurityRegistryRecord({ holders }));
    }

    const unitholdersRecords = this.unitholdersFormArray.value
      .map((shareholderForm) => {
        let holders: (IndividualHolderModel | CorporateHolderModel)[] = [];
        const holderType = shareholderForm.shareholderType;

        if (holderType === ShareholderTypesEnum.Corporate) {
          const entityData = new EntityData({
            name: shareholderForm.name ?? '',
            entityNumber: shareholderForm.acn ?? '',
            registeredAddress: new Address(shareholderForm.registeredAddress as Partial<Address>),
          });
          const holder = new CorporateHolderModel({
            entityData,
            relationshipId: shareholderForm.relationshipId || Guid.generate()
          });
          holder.details.authorisedSignatories = shareholderForm.authSignatories ?? [];
          holders = [holder];
        } else if (shareholderForm.joint) {
          holders = shareholderForm.joint
            .filter((holder) => holder.$type === CorporateHolderModel.$type)
            .map((holder) => {

              holder.relationshipId = holder.relationshipId || Guid.generate();
              return holder as CorporateHolderModel;
            });
        }

        return new SecurityRegistryRecord({ holders });
      });

    records.push(...unitholdersRecords);
    this.authorisedSecurityRegistryRecords = records;
  }

  get trustDetailsForm() {
    return this.stepperForm.controls[this.StepsEnum.TrustDetailsAndAddress];
  }

  override get trusteesFormArray() {
    return this.stepperForm.controls[this.StepsEnum.Trustees].controls.trustees;
  }

  get unitholdersFormArray() {
    return this.stepperForm.controls[this.StepsEnum.Unitholders].controls.unitholders;
  }

  get authorisedSignatoriesForm() {
    return this.stepperForm.controls[this.StepsEnum.AuthorisedSignatories];
  }
}
